import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';

import {
  APP_DARK_CLASS,
  isThemeMode,
  PREFERS_DARK_COLOR_SCHEME_QUERY,
  THEME_STORAGE_KEY,
} from './theme.types';
import type { ResolvedThemeMode, ThemeMode } from './theme.types';

/**
 * Application-wide appearance service that resolves and persists light/dark preferences.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly modeState = signal<ThemeMode>('system');
  private readonly systemPrefersDarkState = signal(false);

  /** Persisted user preference (`system`, `light`, or `dark`). */
  readonly mode = this.modeState.asReadonly();

  /** Resolved runtime mode after evaluating system preference for `system`. */
  readonly resolvedMode = computed<ResolvedThemeMode>(() => {
    const mode = this.modeState();

    if (mode === 'system') {
      return this.systemPrefersDarkState() ? 'dark' : 'light';
    }

    return mode;
  });

  /** Convenience flag for templates and imperative UI logic. */
  readonly isDark = computed(() => this.resolvedMode() === 'dark');

  constructor() {
    if (this.isBrowser) {
      this.modeState.set(this.readStoredMode());
      this.bindSystemPreferenceListener();
    }

    this.applyModeToDocument(this.resolvedMode());

    effect(() => {
      const mode = this.modeState();
      const resolvedMode = this.resolvedMode();

      this.applyModeToDocument(resolvedMode);
      this.persistMode(mode);
    });
  }

  /** Ensures mode side effects are initialized during app startup. */
  initialize(): void {
    this.applyModeToDocument(this.resolvedMode());
  }

  /** Updates and persists the selected appearance mode. */
  setMode(mode: ThemeMode): void {
    this.modeState.set(mode);
  }

  /** Sets up browser listener for system-level color-scheme changes. */
  private bindSystemPreferenceListener(): void {
    if (!this.hasMatchMedia()) {
      this.systemPrefersDarkState.set(false);
      return;
    }

    const mediaQueryList = window.matchMedia(PREFERS_DARK_COLOR_SCHEME_QUERY);
    this.systemPrefersDarkState.set(mediaQueryList.matches);

    const listener = (event: MediaQueryListEvent): void => {
      this.systemPrefersDarkState.set(event.matches);
    };

    mediaQueryList.addEventListener('change', listener);

    this.destroyRef.onDestroy(() => {
      mediaQueryList.removeEventListener('change', listener);
    });
  }

  /** Reads the persisted mode from local storage and validates it. */
  private readStoredMode(): ThemeMode {
    if (!this.isBrowser) {
      return 'system';
    }

    try {
      const persistedMode = window.localStorage.getItem(THEME_STORAGE_KEY);

      return isThemeMode(persistedMode) ? persistedMode : 'system';
    } catch {
      return 'system';
    }
  }

  /** Persists the selected mode in local storage for future sessions. */
  private persistMode(mode: ThemeMode): void {
    if (!this.isBrowser) {
      return;
    }

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // Ignore storage quota and privacy-mode write failures.
    }
  }

  /** Applies the resolved mode to document-level selectors and native controls. */
  private applyModeToDocument(mode: ResolvedThemeMode): void {
    const rootElement = this.document.documentElement;

    rootElement.classList.toggle(APP_DARK_CLASS, mode === 'dark');
    rootElement.style.colorScheme = mode;
  }

  /** Returns whether `window.matchMedia` is available. */
  private hasMatchMedia(): boolean {
    return typeof window.matchMedia === 'function';
  }
}
